import { REPORT } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { getUniqueDates } from '@utils/data';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface MaterialTypeCutPctByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const MaterialTypeCutPctByMonth = ({ dateRange }: MaterialTypeCutPctByMonthProps) => {
    const [range, setRange] = useRange('MaterialTypeCutPctByMonth');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_MATERIAL_TYPE_CUT_PERCENTAGE_OVER_TIME,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);
    const dates = dateKeys.map(({ date }) => date);
    const productNames = Array.from(new Set(data.map((item) => item.productName))).sort();
    const valueMap = data.reduce((acc, item) => {
        acc[`${item.productName}|${item.date}`] = item;
        return acc;
    }, {} as Record<string, (typeof data)[number]>);

    const buildSeries = (key: 'productPct' | 'globalProductPct', labelSuffix = '') =>
        productNames.map((productName) => ({
            name: `${productName}${labelSuffix}`,
            data: dates.map((date) => valueMap[`${productName}|${date}`]?.[key] ?? 0),
        }));

    return (
        <BaseReport
            setRange={setRange}
            title={'Material Type Cut % by Month'}
            description={
                'This graph shows the % breakdown of each material type by month, allowing you to compare them to the global average.'
            }
            note={'Note: Fusion data includes both Fusion and Fusion for Curved Devices.'}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            loading={loading}
            hasData={hasData}
            tableOptions={{
                headers: ['Date', 'Product Name', 'Product %', 'Global Avg'],
                data: data.map((item) => [item.date, item.productName, item.productPct, item.globalProductPct]),
                summaries: [countUnique, countUnique, average, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                300,
                [{ name: 'Material Type Cut %', opposite: false }],
                dateKeys,
                [...buildSeries('productPct'), ...buildSeries('globalProductPct', ' Global')],
                undefined,
                {
                    legend: {
                        align: 'right',
                        verticalAlign: 'top',
                    },
                }
            )}
        />
    );
};
